// Date utility helpers for the Athlynx Calendar.
// Week starts on Monday. Day/month names follow the persisted UI language.

const _en = () => { try { return localStorage.getItem("athlynx_lang") === "en"; } catch { return false; } };

export const FR_DAYS_SHORT = _en()
  ? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
  : ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
export const FR_DAYS_LONG = _en()
  ? ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
  : ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
export const FR_MONTHS = _en()
  ? ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
  : ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

export const HOURS = Array.from({ length: 13 }, (_, i) => 8 + i); // 08:00 .. 20:00
export const HOUR_PX = 64; // height of one hour row in week/day view

export function startOfWeek(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const dow = (d.getDay() + 6) % 7; // Mon=0
  d.setDate(d.getDate() - dow);
  return d;
}

export function endOfWeek(date) {
  const s = startOfWeek(date);
  const e = new Date(s);
  e.setDate(s.getDate() + 6);
  e.setHours(23, 59, 59, 999);
  return e;
}

export function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

export function isSameDay(a, b) {
  return a && b && a.toDateString() === b.toDateString();
}

export function formatWeekLabel(weekStart) {
  const start = new Date(weekStart);
  const end = addDays(start, 6);
  const sameMonth = start.getMonth() === end.getMonth();
  const sameYear = start.getFullYear() === end.getFullYear();
  const sDay = start.getDate();
  const eDay = end.getDate();
  const sMonth = FR_MONTHS[start.getMonth()];
  const eMonth = FR_MONTHS[end.getMonth()];
  const sYear = start.getFullYear();
  const eYear = end.getFullYear();
  if (sameMonth && sameYear) return `${sDay} – ${eDay} ${sMonth} ${sYear}`;
  if (sameYear) return `${sDay} ${sMonth} – ${eDay} ${eMonth} ${sYear}`;
  return `${sDay} ${sMonth} ${sYear} – ${eDay} ${eMonth} ${eYear}`;
}

export function formatDayLong(date) {
  const d = new Date(date);
  return `${FR_DAYS_LONG[(d.getDay() + 6) % 7]} ${d.getDate()} ${FR_MONTHS[d.getMonth()]}`;
}

export function formatHM(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function formatRange(startsAt, endsAt) {
  if (!endsAt) return formatHM(startsAt);
  return `${formatHM(startsAt)} – ${formatHM(endsAt)}`;
}

// Returns top offset (px) and height (px) within the day column.
// Anchor is HOURS[0] (08:00). Min visible height: 28px.
export function eventGeometry(startsAt, endsAt) {
  const s = new Date(startsAt);
  const e = endsAt ? new Date(endsAt) : new Date(s.getTime() + 60 * 60 * 1000);
  const dayStartMin = HOURS[0] * 60;
  const dayEndMin = (HOURS[HOURS.length - 1] + 1) * 60;
  const sMin = s.getHours() * 60 + s.getMinutes();
  const eMin = e.getHours() * 60 + e.getMinutes();
  const clampedS = Math.max(sMin, dayStartMin);
  const clampedE = Math.min(eMin, dayEndMin);
  const top = ((clampedS - dayStartMin) / 60) * HOUR_PX;
  const height = Math.max(28, ((clampedE - clampedS) / 60) * HOUR_PX);
  return { top, height };
}

// Build month grid (always 6 rows × 7 cols, starting Monday)
export function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const start = new Date(first);
  const day = (first.getDay() + 6) % 7;
  start.setDate(first.getDate() - day);
  const arr = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    arr.push(d);
  }
  return arr;
}

export function buildWeekDays(weekStart) {
  return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
}

// Group events by yyyy-mm-dd
export function groupByDay(events) {
  const m = {};
  events.forEach((e) => {
    const d = new Date(e.starts_at);
    const k = d.toDateString();
    m[k] = m[k] || [];
    m[k].push(e);
  });
  Object.values(m).forEach((arr) => arr.sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)));
  return m;
}

// Format a Date as ISO local string (YYYY-MM-DDTHH:MM) for <input type="datetime-local">.
export function toLocalInput(date) {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Returns the GMT offset label, e.g. "GMT+2"
export function gmtOffsetLabel() {
  const offsetMin = -new Date().getTimezoneOffset();
  const sign = offsetMin >= 0 ? "+" : "-";
  const hours = Math.floor(Math.abs(offsetMin) / 60);
  return `GMT${sign}${hours}`;
}
